import React from "react";
import { Link } from "react-router-dom";
import { Lightbulb, Sparkle, ArrowRight } from "@phosphor-icons/react";

/**
 * Universal nudge toward the IdeaLab discovery wizard.
 *
 * Variants:
 *   - "banner"  → gradient hero-slot banner (matches the Active-Promotion style).
 *                 Use this directly under a page hero as the prime CTA.
 *   - "dark"    → full-bleed dark section (good mid-page break).
 *   - "light"   → subtle light section with top/bottom hairlines.
 */
export default function IdeaLabPromo({ variant = "dark", className = "" }) {
  if (variant === "banner") return <BannerVariant className={className} />;

  const dark = variant === "dark";
  return (
    <section className={`${dark ? "bg-zinc-950 text-white" : "bg-zinc-50 text-zinc-900 border-y border-zinc-200"} ${className}`} data-testid="idealab-promo">
      <div className="app-container py-14 md:py-16 grid grid-cols-1 md:grid-cols-12 gap-8 items-center">
        <div className="md:col-span-8">
          <div className={`inline-flex items-center gap-2 px-3 py-1.5 mb-5 ${dark ? "bg-[hsl(var(--appistry-signal))]" : "bg-zinc-950 text-white"}`}>
            <Lightbulb size={14} weight="fill" />
            <span className="text-[10px] font-mono uppercase tracking-[2.5px] font-bold">NEW · APPISTRY IDEALAB™</span>
          </div>
          <h3 className="text-2xl md:text-3xl font-bold tracking-tight leading-tight" style={{ fontFamily: "'Outfit', sans-serif" }}>
            Got an idea worth exploring? Let&apos;s shape it together.
          </h3>
          <p className={`mt-3 max-w-xl ${dark ? "text-zinc-300" : "text-zinc-600"}`}>
            11 short steps · roughly 10–30 minutes depending on how much detail you share. A consultant-led AI helps you articulate it and emails you a branded discovery pack the moment you submit — yours to keep.
          </p>
        </div>
        <div className="md:col-span-4 flex md:justify-end">
          <Link to="/idealab" className={dark ? "btn-signal" : "btn-ghost-ink"} data-testid="idealab-promo-cta">
            Start a discovery session <ArrowRight size={14} weight="bold" />
          </Link>
        </div>
      </div>
    </section>
  );
}

function BannerVariant({ className }) {
  return (
    <section
      className={`border-b border-zinc-200 bg-gradient-to-r from-[hsl(var(--appistry-signal))] to-[hsl(var(--appistry-teal))] text-white ${className}`}
      data-testid="idealab-promo"
    >
      <div className="relative app-container py-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <div className="h-10 w-10 bg-white/15 border border-white/25 flex items-center justify-center">
            <Sparkle size={20} weight="fill" color="white" />
          </div>
          <div>
            <div className="overline text-white/80">New · Appistry IdeaLab™</div>
            <div className="text-lg font-bold tracking-tight text-white" style={{ fontFamily: "'Outfit', sans-serif" }}>
              Got an idea worth exploring? Let&apos;s shape it together.
            </div>
            <div className="text-sm text-white/85 mt-0.5">
              11 short steps · roughly 10–30 minutes · consultant-led AI, and you walk away with a branded discovery pack.
            </div>
          </div>
        </div>
        <Link
          to="/idealab"
          className="inline-flex items-center gap-2 bg-white text-[hsl(var(--appistry-signal))] px-5 py-3 font-semibold hover:bg-zinc-50 transition-colors"
          data-testid="idealab-promo-cta"
        >
          Start a discovery session
          <ArrowRight size={16} weight="bold" />
        </Link>
      </div>
    </section>
  );
}
